import React from 'react';
import {
    LuLayers, LuRefreshCw, LuWallet, LuCalendarDays, LuReceiptText, LuTriangleAlert,
    LuCalendarClock, LuCoins, LuChartColumn, LuChartPie, LuCircleX
} from 'react-icons/lu';
import {
    splitCurrencyTotals,
    formatAmount,
    formatDate,
    formatDateShort,
    relativeDay,
    cycleLabel
} from './paymentConstants';
import ServiceAvatar from './ServiceAvatar';

// The landing tab. It exists to answer, without scrolling or clicking:
//
//   What am I paying for?  How much?  What charges me next, and when?
//   Which charges do I still need to confirm?  Is a card about to break them?
//
// Everything here is summary plus the one-click action for each item — confirm
// a charge, record an ad top-up. Anything longer opens a modal.

// `color` tints the icon tile; `tone` colours the value when it needs attention
const KpiTile = ({ icon: Icon, color, label, value, sub, extra, tone }) => (
    <div className={`kpi-tile ${tone ? `kpi-${tone}` : ''}`}>
        <span className={`kpi-icon kpi-icon-${color}`}><Icon /></span>
        <div className="kpi-text">
            <span className="kpi-label">{label}</span>
            <span className="kpi-value">{value}</span>
            {extra?.length > 0 && <span className="kpi-extra">+ {extra.join(' · ')}</span>}
            {sub && <span className="kpi-count">{sub}</span>}
        </div>
    </div>
);

const PanelHeader = ({ icon: Icon, title, hint, onViewAll }) => (
    <div className="ov-panel-header">
        <Icon className="ov-panel-icon" />
        <div className="ov-panel-heading">
            <h2>{title}</h2>
            {hint && <span>{hint}</span>}
        </div>
        {onViewAll && <button className="ov-view-all" onClick={onViewAll}>View all</button>}
    </div>
);

const PanelEmpty = ({ icon: Icon, title, children }) => (
    <div className="ov-empty">
        <Icon className="ov-empty-icon" />
        <strong>{title}</strong>
        {children && <span>{children}</span>}
    </div>
);

// Upcoming renewals are PROJECTED from each subscription's renewal date. None
// of this is money that has been spent — it is what is about to be charged.
// A past date splits two ways: auto-renew = "charged, confirm it"; manual =
// genuinely overdue.
const bucketFor = (item) => {
    if (item.daysUntil < 0) return item.autoRenew ? 'To confirm' : 'Overdue';
    if (item.daysUntil === 0) return 'Today';
    if (item.daysUntil === 1) return 'Tomorrow';
    if (item.daysUntil <= 7) return 'This week';
    if (item.daysUntil <= 30) return 'This month';
    return 'Later';
};

const BUCKET_ORDER = ['Overdue', 'To confirm', 'Today', 'Tomorrow', 'This week', 'This month', 'Later'];

const monthName = (m, y) => new Date(y, m - 1, 1).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' });

const PaymentsOverview = ({
    summary, upcoming, failedPayments, variableRecords = [],
    canCreate, onPay, onConfirm, onOpen, onAdd, onRecordCharge, onManageCards, onViewAll
}) => {
    if (!summary) {
        return <div className="loading-state">Loading overview...</div>;
    }

    const monthly = splitCurrencyTotals(summary.monthlyRecurring, true);
    const yearly = splitCurrencyTotals(summary.yearlyRecurring, true);
    const overdue = splitCurrencyTotals(summary.overdue, true);
    const toConfirm = splitCurrencyTotals(summary.toConfirm, true);
    const spent = splitCurrencyTotals(summary.totalSpendThisMonth, true);
    const variable = splitCurrencyTotals(summary.variableSpend, true);

    const buckets = new Map();
    for (const item of upcoming) {
        const key = bucketFor(item);
        if (!buckets.has(key)) buckets.set(key, []);
        buckets.get(key).push(item);
    }
    const orderedBuckets = BUCKET_ORDER.filter(b => buckets.has(b)).map(b => [b, buckets.get(b)]);

    // Spend by category, monthly-recurring basis, largest first
    // Each bar is labelled in its own currency (QAR first, as everywhere else).
    // Its length uses that primary figure, so bars in different currencies are
    // only roughly comparable — nothing is converted.
    const categoryRows = Object.entries(summary.byCategory || {})
        .map(([name, totals]) => {
            const currency = totals.QAR > 0
                ? 'QAR'
                : Object.keys(totals).sort((a, b) => totals[b] - totals[a])[0] || 'QAR';
            return { name, currency, total: totals[currency] || 0, label: splitCurrencyTotals(totals) };
        })
        .filter(row => row.total > 0)
        .sort((a, b) => b.total - a.total);
    const categoryMax = categoryRows.length ? categoryRows[0].total : 0;

    const nothingAtAll =
        summary.activeSubscriptions === 0 &&
        summary.variableCount === 0 &&
        summary.onetimeCount === 0;

    if (nothingAtAll) {
        return (
            <div className="empty-state empty-state-lg">
                <div className="empty-state-icon">💳</div>
                <h3>Nothing tracked yet</h3>
                <p>
                    Add the services the company pays for online — Meta Ads, Google Ads, Hostinger, Claude, OpenAI,
                    app stores — and every renewal, card charge and expiring card shows up here.
                </p>
                {canCreate && (
                    <button className="btn btn-primary" onClick={onAdd}>+ Add your first service</button>
                )}
            </div>
        );
    }

    const cardsExpiring = summary.cardsExpiring || [];

    // Only one action per upcoming row: the first occurrence of each service
    // gets it, later projected cycles of the same service do not.
    const seen = new Set();

    return (
        <>
            <div className="kpi-strip">
                <KpiTile
                    icon={LuLayers}
                    color="rose"
                    label="Active services"
                    value={summary.activeSubscriptions + summary.variableCount}
                    sub={`${summary.activeSubscriptions} recurring · ${summary.autoRenewOnCount} auto-renew`}
                />
                <KpiTile
                    icon={LuRefreshCw}
                    color="green"
                    label="Recurring / month"
                    value={monthly.primary}
                    extra={monthly.extra}
                    sub={`≈ ${yearly.primary} a year`}
                />
                <KpiTile
                    icon={LuWallet}
                    color="blue"
                    label={`Spent in ${summary.monthLabel}`}
                    value={spent.primary}
                    extra={spent.extra}
                    sub={summary.variablePaymentCount > 0 ? `incl. ${variable.primary} pay-as-you-go` : 'recorded payments only'}
                />
                <KpiTile
                    icon={LuCalendarDays}
                    color="amber"
                    label="Renewing this week"
                    value={summary.renewingThisWeek}
                    sub={summary.renewingThisWeek === 1 ? 'renewal' : 'renewals'}
                    tone={summary.renewingThisWeek > 0 ? 'warning' : null}
                />
                {/* Only when there is something to act on — an all-clear tile
                    is noise beside the four that always matter */}
                {summary.toConfirmCount > 0 && (
                    <KpiTile
                        icon={LuReceiptText}
                        color="blue"
                        label="Charges to confirm"
                        value={toConfirm.primary}
                        sub={`${summary.toConfirmCount} auto-renewal(s) passed`}
                        tone="info"
                    />
                )}
                {summary.overdueCount > 0 && (
                    <KpiTile
                        icon={LuTriangleAlert}
                        color="red"
                        label="Overdue (manual)"
                        value={overdue.primary}
                        sub={`${summary.overdueCount} unpaid`}
                        tone="danger"
                    />
                )}
            </div>

            {/* Things that need a decision, before anything else */}
            {(cardsExpiring.length > 0 || summary.trialsEndingSoon > 0 || summary.failedCount > 0 || summary.toConfirmCount > 0) && (
                <div className="attention-strip">
                    {cardsExpiring.map(card => (
                        <div key={card._id} className={`attention-item ${card.expired ? 'attention-danger' : 'attention-warning'}`}>
                            💳 <strong>{card.label}</strong> {card.expired ? 'expired' : 'expires'} {monthName(card.expiryMonth, card.expiryYear)}
                            {card.linkedCount > 0
                                ? <> — still charged by <strong>{card.linkedCount}</strong> service{card.linkedCount === 1 ? '' : 's'} ({card.linkedNames.slice(0, 5).join(', ')}{card.linkedCount > 5 ? '…' : ''}). Update the card at each vendor{card.expired ? ' now — their charges will fail' : ' before then'}.</>
                                : '.'}
                            {onManageCards && (
                                <button className="link-button attention-link" onClick={onManageCards}>Manage cards</button>
                            )}
                        </div>
                    ))}
                    {summary.toConfirmCount > 0 && (
                        <div className="attention-item attention-info">
                            🧾 <strong>{summary.toConfirmCount}</strong> auto-renewal{summary.toConfirmCount === 1 ? '' : 's'} passed
                            their date — check the card statement and press <strong>✓ Confirm</strong> below (or record a failure).
                        </div>
                    )}
                    {summary.trialsEndingSoon > 0 && (
                        <div className="attention-item attention-warning">
                            ⏳ <strong>{summary.trialsEndingSoon}</strong> free trial{summary.trialsEndingSoon === 1 ? '' : 's'} ending
                            within 7 days — cancel before {summary.trialsEndingSoon === 1 ? 'it converts' : 'they convert'} to paid.
                        </div>
                    )}
                    {summary.failedCount > 0 && (
                        <div className="attention-item attention-danger">
                            ✕ <strong>{summary.failedCount}</strong> failed payment{summary.failedCount === 1 ? '' : 's'} — the charge did not go through.
                        </div>
                    )}
                </div>
            )}

            <div className="overview-columns">
                <section className="overview-panel">
                    <PanelHeader
                        icon={LuCalendarDays}
                        title="Renewals"
                        hint="Expected charges — not money spent until confirmed"
                        onViewAll={onViewAll && (() => onViewAll('subscription'))}
                    />

                    {orderedBuckets.length === 0 ? (
                        <PanelEmpty icon={LuCalendarClock} title="You're all clear — no upcoming renewals.">
                            When you add services with a renewal date, they will appear here so you can keep track of upcoming charges.
                        </PanelEmpty>
                    ) : (
                        orderedBuckets.map(([bucket, items]) => (
                            <div key={bucket} className="upcoming-bucket">
                                <div className={`upcoming-bucket-label ${bucket === 'Overdue' ? 'upcoming-overdue' : bucket === 'To confirm' ? 'upcoming-confirm' : ''}`}>
                                    {bucket}
                                </div>
                                {items.map((item, index) => {
                                    const first = !seen.has(String(item.subscriptionId));
                                    seen.add(String(item.subscriptionId));
                                    const confirmable = item.autoRenew && item.daysUntil <= 0;
                                    return (
                                        <div
                                            key={`${item.subscriptionId}-${index}`}
                                            className="upcoming-row"
                                            onClick={() => onOpen(item.subscriptionId)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => { if (e.key === 'Enter') onOpen(item.subscriptionId); }}
                                        >
                                            <ServiceAvatar record={item} size="sm" />
                                            <div className="upcoming-main">
                                                <div className="upcoming-name">
                                                    {item.name}
                                                    {item.isTrial && <span className="status-badge status-trial">Trial</span>}
                                                </div>
                                                <div className="upcoming-meta">
                                                    {formatDateShort(item.dueDate)} · {relativeDay(item.daysUntil)} · {cycleLabel(item)}
                                                    {(item.cardLabel || item.paymentMethod) && ` · 💳 ${item.cardLabel || item.paymentMethod}`}
                                                    {item.branchName && ` · ${item.branchName}`}
                                                </div>
                                            </div>
                                            <div className={`autorenew-pill autorenew-sm ${item.autoRenew ? 'autorenew-on' : 'autorenew-off'}`}>
                                                <span className="autorenew-dot" />
                                                {item.autoRenew ? 'Auto' : 'Manual'}
                                            </div>
                                            <div className="upcoming-amount">{formatAmount(item.amount, item.currency)}</div>
                                            {canCreate && first && (
                                                confirmable ? (
                                                    <button
                                                        className="btn btn-sm btn-success"
                                                        title={`Record ${formatAmount(item.amount, item.currency)} charged on ${formatDate(item.dueDate)}`}
                                                        onClick={(e) => { e.stopPropagation(); onConfirm(item.subscriptionId, item.dueDate); }}
                                                    >
                                                        ✓ Confirm
                                                    </button>
                                                ) : item.daysUntil <= 7 ? (
                                                    <button
                                                        className="btn btn-sm btn-secondary"
                                                        onClick={(e) => { e.stopPropagation(); onPay(item.subscriptionId); }}
                                                    >
                                                        Mark Paid
                                                    </button>
                                                ) : null
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ))
                    )}
                </section>

                <section className="overview-panel">
                    <PanelHeader
                        icon={LuCoins}
                        title="Pay-as-you-go"
                        hint="Ads, API credits — record each charge"
                        onViewAll={onViewAll && (() => onViewAll('usage'))}
                    />
                    {variableRecords.length === 0 ? (
                        <PanelEmpty icon={LuReceiptText} title="No pay-as-you-go services yet.">
                            Meta Ads, Google Ads and API credits go here. Record a charge to get started.
                        </PanelEmpty>
                    ) : (
                        <div className="payg-list">
                            {variableRecords.map(record => (
                                <div key={record._id} className="payg-row">
                                    <ServiceAvatar record={record} size="sm" />
                                    <div className="upcoming-main">
                                        <div className="upcoming-name">{record.name}</div>
                                        <div className="upcoming-meta">
                                            {record.lastPaidDate ? `Last charge ${formatDate(record.lastPaidDate)}` : 'No charges recorded yet'}
                                            {record.paymentMethod && ` · 💳 ${record.paymentMethod}`}
                                        </div>
                                    </div>
                                    {record.billingUrl && (
                                        <a className="btn btn-sm btn-ghost" href={record.billingUrl} target="_blank" rel="noopener noreferrer"
                                           title="Open the billing page to see the latest charge">↗</a>
                                    )}
                                    {canCreate && (
                                        <button className="btn btn-sm btn-success" onClick={() => onRecordCharge(record)}>+ Charge</button>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                </section>
            </div>

            <section className="overview-panel overview-panel-wide">
                    <PanelHeader
                        icon={LuChartColumn}
                        title="Where the money goes"
                        hint="Recurring, per month"
                        onViewAll={onViewAll && categoryRows.length > 0 ? () => onViewAll('subscription') : undefined}
                    />

                    {categoryRows.length === 0 ? (
                        <PanelEmpty icon={LuChartPie} title="No recurring subscriptions yet.">
                            Add services to see a breakdown of your monthly costs here.
                        </PanelEmpty>
                    ) : (
                        <div className="category-bars">
                            {categoryRows.map(row => (
                                <div key={row.name} className="category-bar-row">
                                    <div className="category-bar-head">
                                        <span>{row.name}</span>
                                        <strong>
                                            {row.label.primary}
                                            {row.label.extra.length > 0 && (
                                                <span className="kpi-extra"> + {row.label.extra.join(' · ')}</span>
                                            )}
                                        </strong>
                                    </div>
                                    <div className="category-bar-track">
                                        <div
                                            className="category-bar-fill"
                                            style={{ width: `${categoryMax ? Math.max(3, (row.total / categoryMax) * 100) : 0}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {failedPayments.length > 0 && (
                        <>
                            <div className="pay-group-header-spaced">
                                <PanelHeader icon={LuCircleX} title="Failed payments" hint="Charge did not go through" />
                            </div>
                            <div className="failed-list">
                                {failedPayments.slice(0, 5).map(p => (
                                    <div key={p._id} className="failed-row">
                                        <span className="failed-name">{p.subscriptionName}</span>
                                        <span className="failed-meta">
                                            {formatDateShort(p.paidDate)}
                                            {p.cardLabel && ` · ${p.cardLabel}`}
                                            {p.failureReason && ` · ${p.failureReason}`}
                                        </span>
                                        <strong>{formatAmount(p.amount, p.currency)}</strong>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
            </section>
        </>
    );
};

export default PaymentsOverview;
